<script setup>
import { useTemplateRef, onBeforeUnmount, onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useConversationFilterContext } from './provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConditionRow from './ConditionRow.vue';

const props = defineProps({
  isFolderView: {
    type: Boolean,
    default: false,
  },
  folderName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['applyFilter', 'updateFolder', 'close']);
const { filterTypes } = useConversationFilterContext();

const filters = defineModel({
  type: Array,
  default: [],
});
const folderNameLocal = ref(props.folderName);

const DEFAULT_FILTER = {
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};

const { t } = useI18n();
const store = useStore();

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_FILTER }];
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedCustomViews = () => {
  if (isConditionsValid()) {
    emit('updateFolder', filters.value, folderNameLocal.value);
  }
};

function validateAndSubmit() {
  if (!isConditionsValid()) {
    return;
  }

  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  emit('applyFilter', filters.value);
  useTrack(CONVERSATION_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isFolderView
    ? t('FILTER.TITLE')
    : t('FILTER.EDIT_CUSTOM_FILTER');
});

const handleEscape = event => {
  if (event.key === 'Escape') emit('close');
};
onMounted(() => {
  document.addEventListener('keydown', handleEscape);
  document.body.style.overflow = 'hidden';
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscape);
  document.body.style.overflow = '';
  emit('close');
});
</script>

<template>
  <!-- Backdrop overlay (fixed, ignores parent positioning) -->
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
    @click.self="emit('close')"
  >
    <!-- Modal card -->
    <div
      role="dialog"
      aria-modal="true"
      class="flex flex-col w-full max-w-3xl max-h-[85vh] bg-n-solid-2 border border-n-weak rounded-2xl shadow-2xl"
    >
      <!-- Header -->
      <header
        class="flex flex-shrink-0 items-center justify-between gap-4 px-6 py-4 border-b border-n-weak"
      >
        <div class="flex flex-col gap-0.5 min-w-0">
          <h3
            class="text-base font-semibold tracking-tight leading-tight text-n-slate-12 m-0 truncate"
          >
            {{ filterModalHeaderTitle }}
          </h3>
          <p class="text-[12.5px] text-n-slate-11 m-0">
            {{
              t(
                'FILTER.MODAL_DESCRIPTION',
                'Combine condições para refinar suas conversas.'
              )
            }}
          </p>
        </div>
        <button
          type="button"
          :aria-label="t('GENERAL.CLOSE', 'Fechar')"
          class="flex items-center justify-center shrink-0 size-8 rounded-md text-n-slate-11 hover:bg-n-slate-3 hover:text-n-slate-12 transition-colors"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </header>

      <!-- Body (overflow-visible para não cortar dropdowns dos selects) -->
      <div class="flex flex-col flex-1 gap-4 px-6 py-5 overflow-visible">
        <div v-if="props.isFolderView" class="pb-4 border-b border-n-weak">
          <Input
            v-model="folderNameLocal"
            :label="t('FILTER.FOLDER_LABEL')"
            :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
          />
        </div>

        <ul class="flex flex-col gap-3 list-none p-0 m-0">
          <template v-for="(filter, index) in filters" :key="filter.id">
            <ConditionRow
              v-if="index === 0"
              ref="conditionsRef"
              :key="`filter-${filter.attributeKey}-0`"
              v-model:attribute-key="filter.attributeKey"
              v-model:filter-operator="filter.filterOperator"
              v-model:values="filter.values"
              :filter-types="filterTypes"
              :show-query-operator="false"
              @remove="removeFilter(index)"
            />
            <ConditionRow
              v-else
              :key="`filter-${filter.attributeKey}-${index}`"
              ref="conditionsRef"
              v-model:attribute-key="filter.attributeKey"
              v-model:filter-operator="filter.filterOperator"
              v-model:query-operator="filters[index - 1].queryOperator"
              v-model:values="filter.values"
              show-query-operator
              :filter-types="filterTypes"
              @remove="removeFilter(index)"
            />
          </template>
        </ul>

        <button
          type="button"
          class="flex items-center justify-center gap-1.5 self-start mt-1 px-3 h-9 rounded-md border border-dashed border-n-weak text-[13px] font-medium text-n-slate-11 hover:bg-n-slate-3 hover:text-n-slate-12 hover:border-n-strong transition-colors"
          @click="addFilter"
        >
          <span class="i-lucide-plus size-3.5" />
          {{ $t('FILTER.ADD_NEW_FILTER') }}
        </button>
      </div>

      <!-- Footer (sticky) -->
      <footer
        class="flex flex-shrink-0 flex-wrap gap-2 items-center justify-between px-6 py-4 border-t border-n-weak bg-n-solid-2"
      >
        <Button sm faded slate @click="resetFilter">
          {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
        </Button>
        <div class="flex flex-wrap gap-2">
          <Button sm ghost slate @click="emit('close')">
            {{ t('GENERAL.CANCEL', 'Cancelar') }}
          </Button>
          <Button
            v-if="isFolderView"
            sm
            solid
            blue
            :disabled="!folderNameLocal"
            @click="updateSavedCustomViews"
          >
            {{ t('FILTER.UPDATE_BUTTON_LABEL') }}
          </Button>
          <Button v-else sm solid blue @click="validateAndSubmit">
            {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
          </Button>
        </div>
      </footer>
    </div>
  </div>
</template>
